/**
 * Canvas Studio chrome — all presentational editor UI (no Fabric here).
 * Each piece reads the Zustand store for intent and calls the CanvasCore
 * imperative handle (passed as `api`) for actions.
 *
 *   FloatingToolbar  — left tool palette
 *   TopActionBar     — back / name / save / history / export / grid / bg
 *   ZoomControls     — bottom-right zoom
 *   RightPanel       — selected-object properties (or canvas settings)
 *   ContextMenu      — right-click actions
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft, Undo2, Redo2, Download, Save, PanelRight, Grid3x3, Magnet,
  Plus, Minus, Maximize, Lock, Unlock, Copy, ClipboardPaste, Trash2,
  CopyPlus, BringToFront, SendToBack, Group, Ungroup, Check, Map as MapIcon, LayoutTemplate,
  ChevronUp, History, Play, MessageCircle, Hand, Eye,
} from 'lucide-react';
import { T } from '../../../components/em/ui';
import { useCanvasStore } from './canvasStore';
import {
  TOOL_GROUPS, ALL_TOOLS, BACKGROUNDS, ZOOM_PRESETS, GRID_PRESETS, STICKY_COLORS, FONT_FAMILIES,
} from './canvasConstants';

const surface = {
  background: T.bgCard, border: `1px solid ${T.border}`,
  boxShadow: '0 2px 8px rgba(10,10,10,0.06), 0 16px 40px -16px rgba(10,10,10,0.28)',
  backdropFilter: 'blur(20px) saturate(160%)', WebkitBackdropFilter: 'blur(20px) saturate(160%)',
};

/* ─────────────────────── Floating tool palette (bottom-centre) ──────────────────
   Select + Hand are flat buttons; Shapes / Draw / Objects / Flowchart collapse
   into hover-or-tap flyouts that open above the bar. A group button shows the
   last tool picked from it and re-activates it on click. */
const tipFor = (t) => `${t.label}${t.shortcut ? ` (${t.shortcut})` : ''}`;
const btnStyle = (on) => ({
  background: on ? T.gradient : 'transparent',
  color: on ? '#fff' : T.fg4,
  boxShadow: on ? '0 4px 10px -3px rgba(245,75,1,0.5)' : 'none',
});

// `commentCount` — other people's UNREAD comments on open threads (your own
// never count; opening the comments marks them read), shown as a badge on the
// Comment button.
// `readOnly` — a "Read only" share: only Pan and Comments are offered.
export function FloatingToolbar({ commentCount = 0, readOnly = false }) {
  const activeTool = useCanvasStore((s) => s.activeTool);
  const setActiveTool = useCanvasStore((s) => s.setActiveTool);
  const [openGroup, setOpenGroup] = useState(null);
  const [lastByGroup, setLastByGroup] = useState({});
  const closeTimer = useRef(null);

  const commentBtn = (
    <button
      type="button"
      aria-label="Comments"
      data-tip="Comments"
      onClick={() => setActiveTool(activeTool === 'comment' ? 'select' : 'comment')}
      className="cv-tool-btn"
      style={btnStyle(activeTool === 'comment')}
    >
      <MessageCircle size={18} strokeWidth={1.9} />
      {commentCount > 0 && activeTool !== 'comment' && (
        <span className="cv-comment-count">{commentCount > 99 ? '99+' : commentCount}</span>
      )}
    </button>
  );

  // Remember the active tool within its group (also catches keyboard shortcuts).
  useEffect(() => {
    const g = TOOL_GROUPS.find((grp) => grp.key !== 'select' && grp.tools.some((t) => t.id === activeTool));
    if (g) setLastByGroup((prev) => (prev[g.key] === activeTool ? prev : { ...prev, [g.key]: activeTool }));
  }, [activeTool]);

  const openNow = (key) => { clearTimeout(closeTimer.current); setOpenGroup(key); };
  const closeSoon = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpenGroup(null), 130); };
  const pick = (id) => { setActiveTool(id); setOpenGroup(null); };

  // After every hook (rules of hooks): a read-only board offers Pan + Comments
  // only — every other tool acts as Pan there anyway (CanvasCore).
  if (readOnly) {
    return (
      <div className="cv-toolbar" style={{ ...surface, borderRadius: 16 }}>
        <button type="button" aria-label="Pan" data-tip="Pan (H)" onClick={() => setActiveTool('hand')}
          className="cv-tool-btn" style={btnStyle(activeTool !== 'comment')}>
          <Hand size={18} strokeWidth={1.9} />
        </button>
        <div className="cv-tool-div" />
        {commentBtn}
      </div>
    );
  }

  return (
    <div className="cv-toolbar" style={{ ...surface, borderRadius: 16 }}>
      {TOOL_GROUPS.map((g, gi) => {
        const div = gi > 0 ? <div className="cv-tool-div" key={`${g.key}-d`} /> : null;

        // Selection group → flat buttons.
        if (g.key === 'select') {
          return (
            <React.Fragment key={g.key}>
              {div}
              {g.tools.map((t) => (
                <button key={t.id} type="button" aria-label={t.label} data-tip={tipFor(t)}
                  onClick={() => setActiveTool(t.id)} className="cv-tool-btn" style={btnStyle(activeTool === t.id)}>
                  <t.Icon size={18} strokeWidth={1.9} />
                </button>
              ))}
            </React.Fragment>
          );
        }

        // Other groups → flyout.
        const lastId = lastByGroup[g.key] || g.tools[0].id;
        const lastTool = ALL_TOOLS.find((t) => t.id === lastId) || g.tools[0];
        const GroupIcon = lastTool.Icon;
        const groupActive = g.tools.some((t) => t.id === activeTool);
        const open = openGroup === g.key;

        return (
          <React.Fragment key={g.key}>
            {div}
            <div className="cv-tg" onMouseEnter={() => openNow(g.key)} onMouseLeave={closeSoon}>
              {open && (
                <div className="cv-flyout" style={{ ...surface, borderRadius: 12 }}>
                  {g.tools.map((t) => (
                    <button key={t.id} type="button" aria-label={t.label} data-tip={tipFor(t)}
                      onClick={() => pick(t.id)} className="cv-tool-btn" style={btnStyle(activeTool === t.id)}>
                      <t.Icon size={18} strokeWidth={1.9} />
                    </button>
                  ))}
                </div>
              )}
              <button type="button" aria-label={g.label} className="cv-tool-btn cv-group-btn"
                onClick={() => { setActiveTool(lastId); setOpenGroup(open ? null : g.key); }}
                style={btnStyle(groupActive)}>
                <GroupIcon size={18} strokeWidth={1.9} />
                <ChevronUp size={9} className="cv-caret" style={{ color: groupActive ? 'rgba(255,255,255,0.9)' : T.fg7 }} />
              </button>
            </div>
          </React.Fragment>
        );
      })}

      {/* Comments — toggles comment mode (pins + panel; a click on the board
          places a comment). Click again, or press Esc, to leave it. */}
      <div className="cv-tool-div" />
      {commentBtn}
    </div>
  );
}

/* ───────────────────────────────── Top bar ─────────────────────────────────── */
// `readOnly` — a "Read only" share: no rename, undo/redo, templates, history
// or save; a "View only" chip says why. Present and Export still work.
export function TopActionBar({ name, onRename, onBack, onSave, saving, onExport, onTemplates, onHistory, onPresent, presence, api, readOnly = false }) {
  const { dirty, canUndo, canRedo, objectCount, snapEnabled, toggleSnap, background, setBackground,
    rightPanelOpen, toggleRightPanel, minimapOpen, toggleMinimap } = useCanvasStore();

  const iconBtn = (active) => ({
    width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 9, border: `1px solid ${active ? T.orange : T.border}`,
    background: active ? T.orange50 : 'transparent', color: active ? T.orange : T.fg4,
    cursor: 'pointer',
  });
  const disabled = { opacity: 0.4, pointerEvents: 'none' };

  return (
    <div className="cv-topbar" style={{ ...surface, borderRadius: 14 }}>
      <button type="button" data-tip="Back to all canvases" onClick={onBack} style={{ ...iconBtn(false), width: 'auto', padding: '0 12px', gap: 6, font: `600 13px ${T.font}` }}>
        <ChevronLeft size={17} /> Canvases
      </button>

      <input
        value={name}
        onChange={(e) => onRename(e.target.value)}
        readOnly={readOnly}
        placeholder="Untitled canvas"
        className="cv-name-input"
        style={{ font: `700 15px ${T.font}`, color: T.fg1 }}
      />
      {readOnly && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 9999, background: T.bgMuted, color: T.fg4, font: `600 11.5px ${T.font}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
          <Eye size={13} /> View only
        </span>
      )}

      <div className="cv-topbar-spacer" />

      {presence && <div className="cv-topbar-presence" style={{ marginRight: 6 }}>{presence}</div>}

      <div className="cv-topbar-actions" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        {!readOnly && (
          <>
            <button type="button" data-tip="Undo (⌘Z)" onClick={() => api?.undo()} style={{ ...iconBtn(false), ...(canUndo ? {} : disabled) }}><Undo2 size={17} /></button>
            <button type="button" data-tip="Redo (⌘⇧Z)" onClick={() => api?.redo()} style={{ ...iconBtn(false), ...(canRedo ? {} : disabled) }}><Redo2 size={17} /></button>
            <div className="cv-topbar-div" />
          </>
        )}

        <button type="button" data-tip={snapEnabled ? 'Snap to grid: on' : 'Snap to grid: off'} onClick={toggleSnap} style={iconBtn(snapEnabled)}><Magnet size={16} /></button>
        <BackgroundMenu background={background} setBackground={setBackground} />
        <button type="button" data-tip={minimapOpen ? 'Hide minimap' : 'Show minimap'} onClick={toggleMinimap} style={iconBtn(minimapOpen)}><MapIcon size={16} /></button>
        <button type="button" data-tip={rightPanelOpen ? 'Hide properties panel' : 'Show properties panel'} onClick={toggleRightPanel} style={iconBtn(rightPanelOpen)}><PanelRight size={16} /></button>

        <div className="cv-topbar-div" />

        <span className="cv-count" style={{ font: `600 11px ${T.font}`, color: T.fg5 }}>
          <Grid3x3 size={12} /> {objectCount}
        </span>

        <button type="button" data-tip="Present (step through frames)" onClick={onPresent} style={iconBtn(false)}><Play size={16} /></button>
        {!readOnly && (
          <>
            <button type="button" data-tip="Version history" onClick={onHistory} style={iconBtn(false)}><History size={16} /></button>
            <button type="button" data-tip="Insert a starter template" onClick={onTemplates} style={{ ...iconBtn(false), width: 'auto', padding: '0 12px', gap: 6, font: `600 13px ${T.font}`, color: T.fg2 }}>
              <LayoutTemplate size={16} /> Templates
            </button>
          </>
        )}
        <button type="button" data-tip="Export (PNG, JPEG, SVG, PDF, JSON)" onClick={onExport} style={{ ...iconBtn(false), width: 'auto', padding: '0 12px', gap: 6, font: `600 13px ${T.font}`, color: T.fg2 }}>
          <Download size={16} /> Export
        </button>
        {!readOnly && (
          <button
            type="button" data-tip="Save (⌘S)" onClick={onSave} disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 16px', borderRadius: 9, border: 0, cursor: 'pointer', background: T.gradient, color: '#fff', font: `600 13px ${T.font}`, boxShadow: '0 4px 10px -3px rgba(245,75,1,0.5)' }}
          >
            <Save size={15} /> {saving ? 'Saving…' : 'Save'}
            {dirty && !saving && <span style={{ width: 7, height: 7, borderRadius: 9999, background: '#fff', opacity: 0.95 }} />}
          </button>
        )}
      </div>
    </div>
  );
}

function BackgroundMenu({ background, setBackground }) {
  const [open, setOpen] = React.useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" data-tip="Canvas background" onClick={() => setOpen((v) => !v)}
        style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${T.border}`, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent' }}>
        <span style={{ width: 16, height: 16, borderRadius: 5, border: `1px solid ${T.border}`, background: BACKGROUNDS[background].color || 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50%/8px 8px' }} />
      </button>
      {open && (
        <div style={{ ...surface, position: 'absolute', top: 42, right: 0, borderRadius: 12, padding: 6, zIndex: 50, width: 150 }}>
          {Object.entries(BACKGROUNDS).map(([k, v]) => (
            <button key={k} type="button" onClick={() => { setBackground(k); setOpen(false); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '7px 9px', borderRadius: 8, border: 0, cursor: 'pointer', background: background === k ? T.orange50 : 'transparent', color: T.fg2, font: `500 12px ${T.font}` }}>
              <span style={{ width: 16, height: 16, borderRadius: 5, border: `1px solid ${T.border}`, background: v.color || 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50%/8px 8px' }} />
              {v.label}
              {background === k && <Check size={14} color={T.orange} style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── Zoom controls ─────────────────────────────── */
export function ZoomControls({ api }) {
  const zoom = useCanvasStore((s) => s.zoom);
  const [open, setOpen] = React.useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="cv-zoom" style={{ ...surface, borderRadius: 12 }}>
      <button type="button" data-tip="Zoom out" onClick={() => api?.zoomOut()} className="cv-zoom-btn"><Minus size={16} /></button>
      <div ref={ref} style={{ position: 'relative' }}>
        <button type="button" data-tip="Zoom level" onClick={() => setOpen((v) => !v)} className="cv-zoom-pct" style={{ font: `600 12px ${T.font}`, color: T.fg2 }}>
          {Math.round(zoom * 100)}%
        </button>
        {open && (
          <div style={{ ...surface, position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', borderRadius: 12, padding: 6, width: 132, zIndex: 50 }}>
            {ZOOM_PRESETS.map((z) => (
              <button key={z} type="button" onClick={() => { api?.setZoomLevel(z); setOpen(false); }}
                style={{ width: '100%', textAlign: 'left', padding: '6px 9px', borderRadius: 7, border: 0, cursor: 'pointer', background: 'transparent', color: T.fg2, font: `500 12px ${T.font}` }}>
                {z * 100}%
              </button>
            ))}
            <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
            <button type="button" onClick={() => { api?.fitToScreen(); setOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '6px 9px', borderRadius: 7, border: 0, cursor: 'pointer', background: 'transparent', color: T.fg2, font: `500 12px ${T.font}`, display: 'flex', alignItems: 'center', gap: 7 }}><Maximize size={13} /> Fit to screen</button>
            <button type="button" onClick={() => { api?.resetZoom(); setOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '6px 9px', borderRadius: 7, border: 0, cursor: 'pointer', background: 'transparent', color: T.fg2, font: `500 12px ${T.font}` }}>Reset to 100%</button>
          </div>
        )}
      </div>
      <button type="button" data-tip="Zoom in" onClick={() => api?.zoomIn()} className="cv-zoom-btn"><Plus size={16} /></button>
      <button type="button" data-tip="Fit to screen" onClick={() => api?.fitToScreen()} className="cv-zoom-btn"><Maximize size={15} /></button>
    </div>
  );
}

/* ─────────────────────────────── Right panel ───────────────────────────────── */
const Row = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
    <span style={{ font: `500 12px ${T.font}`, color: T.fg5 }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
  </div>
);
const colorInput = { width: 30, height: 26, border: `1px solid ${T.border}`, borderRadius: 7, padding: 0, background: 'none', cursor: 'pointer' };
const numInput = { width: 62, height: 28, border: `1px solid ${T.border}`, borderRadius: 7, padding: '0 8px', font: `500 12px ${T.font}`, color: T.fg1, background: T.bgCard };
const sectionTitle = { font: `700 11px ${T.font}`, letterSpacing: '0.04em', textTransform: 'uppercase', color: T.fg7, margin: '2px 0 12px' };

export function RightPanel({ api }) {
  const open = useCanvasStore((s) => s.rightPanelOpen);
  const selection = useCanvasStore((s) => s.selection);
  const { background, setBackground, gridSize, setGridSize, snapEnabled, toggleSnap,
    brushColor, brushWidth, setBrush, stickyColorIndex, setStickyColorIndex, activeTool,
    fingerDrawing, setFingerDrawing } = useCanvasStore();

  if (!open) return null;
  const hasSel = selection.count > 0;
  const st = selection.style || {};
  const set = (patch) => api?.applyActiveStyle(patch);

  return (
    <div className="cv-rightpanel" style={{ ...surface, borderRadius: 14 }}>
      {hasSel ? (
        <>
          <div style={sectionTitle}>{selection.count > 1 ? `${selection.count} objects` : (selection.kind || 'Object')}</div>

          {selection.locked && (
            <p style={{ font: `500 11px/1.4 ${T.font}`, color: T.fg5, background: T.bgHead, border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 9px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={12} /> Locked — unlock to edit
            </p>
          )}

          {/* Property edits are blocked while locked; Unlock stays active below. */}
          <div style={selection.locked ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
          {(!st.isText || st.isNode) && (
            <>
              <Row label="Fill">
                <input type="color" value={toHex(st.fill)} onChange={(e) => set({ fill: e.target.value })} style={colorInput} />
              </Row>
              <Row label="Stroke">
                <input type="color" value={toHex(st.stroke)} onChange={(e) => set({ stroke: e.target.value })} style={colorInput} />
              </Row>
              <Row label="Stroke width">
                <input type="number" min={0} max={40} value={st.strokeWidth} onChange={(e) => set({ strokeWidth: Number(e.target.value) })} style={numInput} />
              </Row>
            </>
          )}

          {st.isText && (
            <>
              {st.isNode && <div style={{ ...sectionTitle, marginTop: 10 }}>Text</div>}
              {!st.isNode && (
                <Row label="Text color">
                  <input type="color" value={toHex(st.fill)} onChange={(e) => set({ fill: e.target.value })} style={colorInput} />
                </Row>
              )}
              <Row label="Font">
                <select value={st.fontFamily} onChange={(e) => set({ fontFamily: e.target.value })} style={{ ...numInput, width: 120 }}>
                  {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f.replace(/['"]/g, '').split(',')[0]}</option>)}
                </select>
              </Row>
              <Row label="Size">
                <input type="number" min={6} max={200} value={st.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} style={numInput} />
              </Row>
              <Row label="Weight">
                <select value={st.fontWeight} onChange={(e) => set({ fontWeight: e.target.value })} style={{ ...numInput, width: 92 }}>
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                </select>
              </Row>
              <Row label="Align">
                <select value={st.textAlign} onChange={(e) => set({ textAlign: e.target.value })} style={{ ...numInput, width: 92 }}>
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </Row>
            </>
          )}

          <Row label="Opacity">
            <input type="range" min={0} max={1} step={0.05} value={st.opacity} onChange={(e) => set({ opacity: Number(e.target.value) })} style={{ width: 100 }} />
          </Row>

          <div style={{ height: 1, background: T.border, margin: '8px 0 14px' }} />
          <div style={sectionTitle}>Transform</div>
          <Row label="X"><input type="number" value={st.left} onChange={(e) => set({ left: Number(e.target.value) })} style={numInput} /></Row>
          <Row label="Y"><input type="number" value={st.top} onChange={(e) => set({ top: Number(e.target.value) })} style={numInput} /></Row>
          <Row label="Rotation"><input type="number" value={st.angle} onChange={(e) => set({ angle: Number(e.target.value) })} style={numInput} /></Row>
          <Row label="Size"><span style={{ font: `500 12px ${T.font}`, color: T.fg3 }}>{st.width} × {st.height}</span></Row>
          </div>

          <div style={{ height: 1, background: T.border, margin: '8px 0 12px' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => api?.toggleLockSelected()} className="cv-panel-act" style={{ color: T.fg2 }}>
              {selection.locked ? <Unlock size={14} /> : <Lock size={14} />}{selection.locked ? 'Unlock' : 'Lock'}
            </button>
            <button type="button" disabled={selection.locked} onClick={() => api?.deleteSelected()} className="cv-panel-act" style={{ color: T.danger, ...(selection.locked ? { opacity: 0.4, cursor: 'default' } : {}) }}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={sectionTitle}>Canvas</div>
          <Row label="Background">
            <select value={background} onChange={(e) => setBackground(e.target.value)} style={{ ...numInput, width: 120 }}>
              {Object.entries(BACKGROUNDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Row>
          <Row label="Snap to grid">
            <button type="button" onClick={toggleSnap} style={{ width: 44, height: 24, borderRadius: 9999, border: 0, cursor: 'pointer', background: snapEnabled ? T.orange : T.border, position: 'relative', transition: 'background .15s' }}>
              <span style={{ position: 'absolute', top: 2, left: snapEnabled ? 22 : 2, width: 20, height: 20, borderRadius: 9999, background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
            </button>
          </Row>
          <Row label="Grid size">
            <select value={gridSize} onChange={(e) => setGridSize(Number(e.target.value))} style={{ ...numInput, width: 80 }}>
              {GRID_PRESETS.map((g) => <option key={g} value={g}>{g}px</option>)}
            </select>
          </Row>

          <div style={{ height: 1, background: T.border, margin: '8px 0 14px' }} />
          <div style={sectionTitle}>Drawing</div>
          <Row label="Finger drawing">
            <button type="button" onClick={() => setFingerDrawing(!fingerDrawing)}
              title={fingerDrawing ? 'Finger can draw' : 'Stylus only — one finger pans'}
              style={{ width: 44, height: 24, borderRadius: 9999, border: 0, cursor: 'pointer', background: fingerDrawing ? T.orange : T.border, position: 'relative', transition: 'background .15s' }}>
              <span style={{ position: 'absolute', top: 2, left: fingerDrawing ? 22 : 2, width: 20, height: 20, borderRadius: 9999, background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
            </button>
          </Row>
          <Row label="Brush color"><input type="color" value={toHex(brushColor)} onChange={(e) => setBrush({ brushColor: e.target.value })} style={colorInput} /></Row>
          <Row label={activeTool === 'eraser' ? 'Eraser size' : 'Brush width'}>
            <input type="range" min={1} max={40} value={brushWidth} onChange={(e) => setBrush({ brushWidth: Number(e.target.value) })} style={{ width: 100 }} />
          </Row>

          {activeTool === 'sticky' && (
            <>
              <div style={{ height: 1, background: T.border, margin: '8px 0 14px' }} />
              <div style={sectionTitle}>Sticky color</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {STICKY_COLORS.map((c, i) => (
                  <button key={c.name} type="button" title={c.name} onClick={() => setStickyColorIndex(i)}
                    style={{ width: 26, height: 26, borderRadius: 8, cursor: 'pointer', background: c.bg, border: `2px solid ${stickyColorIndex === i ? T.orange : 'transparent'}` }} />
                ))}
              </div>
            </>
          )}
          <p style={{ font: `400 11px/1.5 ${T.font}`, color: T.fg7, marginTop: 16 }}>
            Select an object to edit its style. Two-finger pinch to zoom, drag with two fingers or Space to pan.
            A stylus always draws and rejects your resting palm; turn off Finger drawing for pencil-only input.
          </p>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────── Context menu ──────────────────────────────── */
export function ContextMenu({ api }) {
  const menu = useCanvasStore((s) => s.contextMenu);
  const setMenu = useCanvasStore((s) => s.setContextMenu);
  const hasClipboard = useCanvasStore((s) => s.hasClipboard);
  const selection = useCanvasStore((s) => s.selection);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const esc = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('keydown', esc); };
  }, [menu, setMenu]);

  if (!menu) return null;
  const run = (fn) => { fn?.(); setMenu(null); };
  const hasTarget = menu.hasTarget;
  const multi = selection.count > 1;
  const isGroup = selection.kind === 'group';

  const Item = ({ icon: Icon, label, onClick, danger, disabled }) => (
    <button type="button" disabled={disabled} onClick={(e) => { e.stopPropagation(); run(onClick); }}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '7px 11px', borderRadius: 7, border: 0, cursor: disabled ? 'default' : 'pointer', background: 'transparent', color: danger ? T.danger : T.fg2, font: `500 12.5px ${T.font}`, opacity: disabled ? 0.4 : 1 }}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div onMouseDown={(e) => e.stopPropagation()} style={{ ...surface, position: 'fixed', left: Math.min(menu.x, window.innerWidth - 190), top: Math.min(menu.y, window.innerHeight - 320), borderRadius: 11, padding: 5, width: 178, zIndex: 100 }}>
      <Item icon={Copy} label="Copy" onClick={() => api?.copySelected()} disabled={!hasTarget} />
      <Item icon={ClipboardPaste} label="Paste" onClick={() => api?.paste()} disabled={!hasClipboard} />
      <Item icon={CopyPlus} label="Duplicate" onClick={() => api?.duplicateSelected()} disabled={!hasTarget} />
      <div style={{ height: 1, background: T.border, margin: '4px 6px' }} />
      <Item icon={BringToFront} label="Bring to front" onClick={() => api?.bringToFront()} disabled={!hasTarget} />
      <Item icon={SendToBack} label="Send to back" onClick={() => api?.sendToBack()} disabled={!hasTarget} />
      <div style={{ height: 1, background: T.border, margin: '4px 6px' }} />
      {multi && <Item icon={Group} label="Group" onClick={() => api?.groupSelected()} />}
      {isGroup && <Item icon={Ungroup} label="Ungroup" onClick={() => api?.ungroupSelected()} />}
      <Item icon={Lock} label={selection.locked ? 'Unlock' : 'Lock'} onClick={() => api?.toggleLockSelected()} disabled={!hasTarget} />
      <div style={{ height: 1, background: T.border, margin: '4px 6px' }} />
      <Item icon={Trash2} label="Delete" onClick={() => api?.deleteSelected()} danger disabled={!hasTarget || selection.locked} />
    </div>
  );
}

/* ─────────────────────────────────── Minimap ───────────────────────────────── */
const MINI_W = 190, MINI_H = 132, MINI_PAD = 10;
const miniMapping = (bounds) => {
  const cw = Math.max(1, bounds.r - bounds.l), ch = Math.max(1, bounds.b - bounds.t);
  const scale = Math.min((MINI_W - MINI_PAD * 2) / cw, (MINI_H - MINI_PAD * 2) / ch);
  const offX = MINI_PAD + ((MINI_W - MINI_PAD * 2) - cw * scale) / 2;
  const offY = MINI_PAD + ((MINI_H - MINI_PAD * 2) - ch * scale) / 2;
  return { scale, offX, offY };
};

export function MiniMap({ api }) {
  const open = useCanvasStore((s) => s.minimapOpen);
  const [state, setState] = useState(null);
  const dragging = useRef(false);

  useEffect(() => {
    if (!open || !api?.getMiniMapState) return;
    const tick = () => setState(api.getMiniMapState());
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [open, api]);

  if (!open) return null;

  const panFromEvent = (e) => {
    if (!state) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const { scale, offX, offY } = miniMapping(state.bounds);
    const sx = state.bounds.l + (e.clientX - rect.left - offX) / scale;
    const sy = state.bounds.t + (e.clientY - rect.top - offY) / scale;
    api.panToScenePoint?.(sx, sy);
  };

  let body = null;
  if (state) {
    const { scale, offX, offY } = miniMapping(state.bounds);
    const X = (x) => offX + (x - state.bounds.l) * scale;
    const Y = (y) => offY + (y - state.bounds.t) * scale;
    body = (
      <>
        {state.items.map((o, i) => (
          <rect key={i} x={X(o.l)} y={Y(o.t)} width={Math.max(1, o.w * scale)} height={Math.max(1, o.h * scale)}
            fill={typeof o.fill === 'string' && o.fill[0] === '#' ? o.fill : '#cbd5e1'} stroke="#94a3b8" strokeWidth="0.5" rx="1" />
        ))}
        <rect x={X(state.view.l)} y={Y(state.view.t)} width={state.view.w * scale} height={state.view.h * scale}
          fill="rgba(245,75,1,0.10)" stroke="#f54b01" strokeWidth="1.5" rx="2" />
      </>
    );
  }

  return (
    <div className="cv-minimap" style={{ ...surface, borderRadius: 12, padding: 5 }}>
      <svg
        width={MINI_W} height={MINI_H} style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
        onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); panFromEvent(e); }}
        onPointerMove={(e) => { if (dragging.current) panFromEvent(e); }}
        onPointerUp={(e) => { dragging.current = false; }}
      >
        <rect x="0" y="0" width={MINI_W} height={MINI_H} rx="8" fill="var(--em-bg-muted, #f1f5f9)" />
        {body}
      </svg>
    </div>
  );
}

// Coerce arbitrary fill values (gradients, patterns, '') to a hex for <input type=color>.
function toHex(v) {
  if (typeof v !== 'string' || !v) return '#000000';
  if (v[0] === '#') return v.length === 7 ? v : '#000000';
  return '#000000';
}
